import { z } from 'zod';
import {
  PANDA_PANIC_MAX_PLAYERS,
  PANDA_PANIC_MIN_PLAYERS,
  PANDA_PANIC_ROOM_CODE_LENGTH,
} from './constants.js';

/**
 * Contrat du JWT de connexion à une salle Panda Panic (spec 15 §6, ADR 0022 §4).
 *
 * L'API NestJS signe un jeton COURT (secret partagé dédié, HS256), le client le
 * présente en rejoignant une salle ; le `onAuth` Colyseus du game-server vérifie
 * la signature LOCALEMENT (aucun compte côté game-server, jamais de cookie sur ce
 * canal — Capacitor, §4). Le jeton ne porte QUE l'identité publique du joueur et
 * la salle visée : aucune donnée cachée (ADR 0003).
 */

/** Motif d'un code de salle : `PANDA_PANIC_ROOM_CODE_LENGTH` lettres majuscules (§6). */
export const PANDA_PANIC_ROOM_CODE_RE = new RegExp(`^[A-Z]{${PANDA_PANIC_ROOM_CODE_LENGTH}}$`);

/** Vrai si `code` est un code de salle bien formé (4 lettres majuscules). */
export function isValidRoomCode(code: string): boolean {
  return PANDA_PANIC_ROOM_CODE_RE.test(code);
}

/** Normalise une saisie utilisateur en code de salle candidat (trim + majuscules). */
export function normalizeRoomCode(input: string): string {
  return input.trim().toUpperCase();
}

/** Schéma d'un code de salle valide. */
export const RoomCodeSchema = z
  .string()
  .length(PANDA_PANIC_ROOM_CODE_LENGTH)
  .regex(PANDA_PANIC_ROOM_CODE_RE, 'code de salle invalide');

/**
 * Claims applicatifs du jeton (au-delà des claims standard `iss`/`aud`/`exp`
 * vérifiés par la couche JWT). `sub` = identité stable (userId d'un compte ou id
 * d'un invité signé) ; `name` = pseudo déjà rendu public par l'API ; `room` lie
 * le jeton à UNE salle précise. `seats` (2–4) ne figure que sur le jeton du
 * CRÉATEUR (l'API le valide et le signe) : nombre total de sièges de la partie,
 * adopté par la salle au `onCreate` — les sièges sans humain deviennent des
 * bots au lancement. Absent → comportement historique (pas de comblement).
 */
export const PandaPanicJoinClaimsSchema = z.object({
  sub: z.string().min(1).max(64),
  name: z.string().min(1).max(40),
  guest: z.boolean().default(false),
  room: RoomCodeSchema,
  seats: z.number().int().min(PANDA_PANIC_MIN_PLAYERS).max(PANDA_PANIC_MAX_PLAYERS).optional(),
});
export type PandaPanicJoinClaims = z.infer<typeof PandaPanicJoinClaimsSchema>;
